const MAX_CASH_COMBINATIONS = 512
const FLOAT_TOLERANCE = 0.000000001

const cashUnit = (currency, fallbackRounding) => {
  if (!currency) return 0

  const configuredUnit = Number(currency.minimum_cash_denomination)
  const minimumUnit = currency.currency_code === 'KHR' ? 1 : currency.currency_code === 'USD' ? 0.01 : 0
  if (configuredUnit > 0) return Math.max(configuredUnit, minimumUnit)
  if (minimumUnit) return minimumUnit

  const configuredRounding = currency.currency_rounding
  const rounding = configuredRounding == null
    ? fallbackRounding
    : Number(configuredRounding)
  const decimals = Number.isFinite(rounding) ? rounding : fallbackRounding
  return Math.pow(10, -Math.max(decimals, 0))
}

const cashCurrency = (code, currency, rate, fallbackRounding) => ({
  code,
  unit: cashUnit(currency, fallbackRounding),
  rate: Number(rate) || 0
})

const changeCombination = (target, preferredUnits, preferred, other) => {
  const preferredAmount = preferredUnits * preferred.unit
  const preferredValue = preferredAmount * preferred.rate
  const otherUnits = other
    ? Math.max(Math.round((target - preferredValue) / (other.unit * other.rate)), 0)
    : 0
  const otherAmount = other ? otherUnits * other.unit : 0
  const otherValue = other ? otherAmount * other.rate : 0

  return {
    amounts: {
      [preferred.code]: preferredAmount,
      ...(other ? { [other.code]: otherAmount } : {})
    },
    difference: Math.abs(preferredValue + otherValue - target)
  }
}

/**
 * Find USD/KHR cash denominations that settle change within base-currency precision.
 * Start with the requested currency, then use the other currency for any remainder.
 *
 * @param {Object} options Amount, currency resources, exchange ratios, and tolerance.
 * @returns {{usd: number, riel: number, balanced: boolean}} Cash change to return.
 */
export const calculateCashChange = ({
  amount,
  preferredCode,
  usdCurrency,
  khrCurrency,
  usdRate,
  khrRate,
  tolerance
}) => {
  const target = Number(amount) || 0
  if (target <= 0) return { usd: 0, riel: 0, balanced: true }

  const currencies = [
    cashCurrency('USD', usdCurrency, usdRate, 2),
    cashCurrency('KHR', khrCurrency, khrRate, 0)
  ].filter(currency => currency.unit > 0 && currency.rate > 0)
  if (!currencies.length) return { usd: 0, riel: 0, balanced: false }

  const preferred = currencies.find(currency => currency.code === preferredCode) || currencies[0]
  const other = currencies.find(currency => currency.code !== preferred.code)
  const firstUnits = Math.ceil(target / (preferred.unit * preferred.rate))
  const lastUnits = Math.max(firstUnits - MAX_CASH_COMBINATIONS, 0)
  const allowedDifference = Number(tolerance) + FLOAT_TOLERANCE
  let best = null

  for (let units = firstUnits; units >= lastUnits; units--) {
    const candidate = changeCombination(target, units, preferred, other)
    if (!best || candidate.difference < best.difference - FLOAT_TOLERANCE) {
      best = candidate
    }
    if (candidate.difference <= allowedDifference) {
      best = candidate
      break
    }
  }

  return {
    usd: Number((best.amounts.USD || 0).toFixed(2)),
    riel: Number((best.amounts.KHR || 0).toFixed(2)),
    balanced: best.difference <= allowedDifference
  }
}

/**
 * Calculate the cash return in the other currency after one split amount changes.
 *
 * @param {Object} options Change due, entered amount and currency rates, and other cash unit.
 * @returns {number} Rounded cash amount in the other currency.
 */
export const calculateOtherChange = ({
  amount,
  entered,
  enteredRate,
  otherRate,
  otherUnit,
  precision
}) => {
  const rate = Number(otherRate) || 0
  const unit = Number(otherUnit) || 0
  if (rate <= 0 || unit <= 0) return 0

  const enteredValue = (Number(entered) || 0) * (Number(enteredRate) || 0)
  const remaining = Math.max((Number(amount) || 0) - enteredValue, 0)
  const roundedAmount = Math.round(remaining / (unit * rate)) * unit
  return Number(roundedAmount.toFixed(precision))
}
